<template><div><component-combinator component-name="DtDatepicker" /><h2 id="usage" tabindex="-1" class="d-docsite--header-2">Usage <a class="header-anchor d-link d-docsite--link d-link" href="#usage" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">We recommend to wrap the datepicker in a <code v-pre>dt-popover</code> component.</p>
<p class="d-docsite--paragraph">It is required to provide the component with proper labels for i18n and accessibility.</p>
<p class="d-docsite--paragraph">The component will return a <a href="https://www.w3schools.com/js/js_dates.asp" class="d-docsite--link d-link" target="_blank" rel="noopener noreferrer">Date object</a> object when a date is selected.</p>
<p class="d-docsite--paragraph">For the <code v-pre>withPopover</code> variant it is necessary to provide the <code v-pre>initial-focus-element=&quot;#prevYearButton&quot;</code> prop
to the <code v-pre>dt-popover</code> component.</p>
<p class="d-docsite--paragraph">With this we accomplish the requirement to have the previous year button focused when the datepicker is opened.</p>
<h2 id="variants-and-examples" tabindex="-1" class="d-docsite--header-2">Variants and Examples <a class="header-anchor d-link d-docsite--link d-link" href="#variants-and-examples" aria-hidden="true">#</a></h2>
<h3 id="default" tabindex="-1" class="d-docsite--header-3">Default <a class="header-anchor d-link d-docsite--link d-link" href="#default" aria-hidden="true">#</a></h3>
<code-example source-code='&lt;dt-datepicker&gt;&lt;/dt-datepicker&gt;'>
<dt-datepicker></dt-datepicker>
</code-example>
<h3 id="with-popover" tabindex="-1" class="d-docsite--header-3">With Popover <a class="header-anchor d-link d-docsite--link d-link" href="#with-popover" aria-hidden="true">#</a></h3>
<code-example source-code='&lt;dt-popover
  :open=&quot;datepickerOpened&quot;
  initial-focus-element=&quot;#prevYearButton&quot;
  padding=&quot;none&quot;
  @opened=&quot;(open) =&gt; { datepickerOpened = open }&quot;
  placement=&quot;bottom-start&quot;
&gt;
  &lt;template #anchor&gt;
    &lt;dt-button
      :size=&quot;200&quot;
      circle
      importance=&quot;clear&quot;
      aria-label=&quot;Open datepicker&quot;
      @click=&quot;toggleDatepicker&quot;
    &gt;
      &lt;template #startIcon&gt;
        &lt;dt-icon
          name=&quot;calendar&quot;
          size=&quot;300&quot;
        /&gt;
      &lt;/template&gt;
    &lt;/dt-button&gt;
  &lt;/template&gt;
  &lt;template #content&gt;
    &lt;dt-datepicker&gt;&lt;/dt-datepicker&gt;
  &lt;/template&gt;
&lt;/dt-popover&gt;'>
<dt-popover
  :open="datepickerOpened"
  initial-focus-element="#prevYearButton"
  padding="none"
  @opened="(open) => { datepickerOpened = open }"
  placement="bottom-start"
>
  <template #anchor>
    <dt-button
      :size="200"
      circle
      importance="clear"
      aria-label="Open datepicker"
      @click="toggleDatepicker"
    >
      <template #startIcon>
        <dt-icon
          name="calendar"
          size="300"
        />
      </template>
    </dt-button>
  </template>
  <template #content>
    <dt-datepicker></dt-datepicker>
  </template>
</dt-popover>
</code-example>
<h3 id="with-min-max-date" tabindex="-1" class="d-docsite--header-3">With min/max date <a class="header-anchor d-link d-docsite--link d-link" href="#with-min-max-date" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">Constrain the selectable date range by providing <code v-pre>min-date</code> and/or <code v-pre>max-date</code> props. Days outside the range are disabled and navigation buttons are disabled when the target month is fully out of range.</p>
<code-example source-code='&lt;script&gt;
const today = new Date();
const minDate = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 5);
const maxDate = new Date(today.getFullYear(), today.getMonth(), today.getDate() + 15);
&lt;/script&gt;
&lt;dt-datepicker
  :selected-date=&quot;new Date()&quot;
  :min-date=&quot;minDate&quot;
  :max-date=&quot;maxDate&quot;
/&gt;'>
<dt-datepicker
  :selected-date="currentSelectedDate"
  :min-date="minDate"
  :max-date="maxDate"
  @selected-date="currentSelectedDate = $event;"
/>
</code-example>
<h2 id="vue-api" tabindex="-1" class="d-docsite--header-2">Vue API <a class="header-anchor d-link d-docsite--link d-link" href="#vue-api" aria-hidden="true">#</a></h2>
<component-vue-api component-name="datepicker"></component-vue-api><h2 id="date-formats" tabindex="-1" class="d-docsite--header-2">Date Formats <a class="header-anchor d-link d-docsite--link d-link" href="#date-formats" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">The following functions are available for date formatting.</p>
<div class="d-bgc-secondary d-bar-400 d-p-200">
  <dt-stack
    :direction="{ 'default': 'column', 'md': 'row' }"
    gap="600"
    align="start"
  >
    <dt-stack align="center">
      <dt-datepicker
        :selected-date="currentSelectedDate"
        @selected-date="currentSelectedDate = $event;"
      />
      <dt-text as="p" kind="body" :size="200" tone="tertiary" align="center" wrap="balance">
        {{ currentSelectedDate }}
      </dt-text>
    </dt-stack>
    <table class="d-table d-bt d-fl1">
      <tbody>
        <tr>
          <th class="d-pis-0" scope="row"><dt-text as="code" kind="code" :size="100" class="d-bgc-transparent">formatLong</dt-text></th>
          <td><dt-text kind="body" :size="100">{{ formatLong(currentSelectedDate) }}</dt-text></td>
        </tr>
        <tr>
          <th class="d-pis-0" scope="row"><dt-text as="code" kind="code" :size="100" class="d-bgc-transparent">formatMedium</dt-text></th>
          <td><dt-text kind="body" :size="100">{{ formatMedium(currentSelectedDate) }}</dt-text></td>
        </tr>
        <tr>
          <th class="d-pis-0" scope="row"><dt-text as="code" kind="code" :size="100" class="d-bgc-transparent">formatShort</dt-text></th>
          <td><dt-text kind="body" :size="100">{{ formatShort(currentSelectedDate) }}</dt-text></td>
        </tr>
        <tr>
          <th class="d-pis-0" scope="row"><dt-text as="code" kind="code" :size="100" class="d-bgc-transparent">formatShort (no weekday)</dt-text></th>
          <td><dt-text kind="body" :size="100">{{ formatShort(currentSelectedDate, false) }}</dt-text></td>
        </tr>
        <tr>
          <th class="d-pis-0" scope="row"><dt-text as="code" kind="code" :size="100" class="d-bgc-transparent">formatNoYear</dt-text></th>
          <td><dt-text kind="body" :size="100">{{ formatNoYear(currentSelectedDate) }}</dt-text></td>
        </tr>
        <tr>
          <th class="d-pis-0" scope="row"><dt-text as="code" kind="code" :size="100" class="d-bgc-transparent">formatNoYear (abbreviated)</dt-text></th>
          <td><dt-text kind="body" :size="100">{{ formatNoYear(currentSelectedDate, true) }}</dt-text></td>
        </tr>
        <tr>
          <th class="d-pis-0" scope="row"><dt-text as="code" kind="code" :size="100" class="d-bgc-transparent">formatNumerical</dt-text></th>
          <td><dt-text kind="body" :size="100">{{ formatNumerical(currentSelectedDate) }}</dt-text></td>
        </tr>
      </tbody>
    </table>
  </dt-stack>
</div>
<!-- TODO: Autogenerate the docs from JSDocs in packages/dialtone-vue/components/datepicker/formatUtils.js -->
<h3 id="formatlong" tabindex="-1" class="d-docsite--header-3">formatLong <a class="header-anchor d-link d-docsite--link d-link" href="#formatlong" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph"><strong>formatLong</strong>(<code v-pre>date</code>, <code v-pre>locale</code>): <code v-pre>string</code></p>
<p class="d-docsite--paragraph">Formats a date into a long format using the specified locale.</p>
<h4 class="d-docsite--header-4">Parameters</h4>
<table>
<thead>
<tr>
<th style="text-align:left">Name</th>
<th style="text-align:left">Type</th>
<th style="text-align:left">Description</th>
</tr>
</thead>
<tbody>
<tr>
<td style="text-align:left"><code v-pre>date</code></td>
<td style="text-align:left"><code v-pre>Date</code></td>
<td style="text-align:left">The date to format</td>
</tr>
<tr>
<td style="text-align:left"><code v-pre>locale</code></td>
<td style="text-align:left"><code v-pre>string</code></td>
<td style="text-align:left">The locale to use for formatting. Defaults to Dialtone preferred locale.</td>
</tr>
</tbody>
</table>
<h4 class="d-docsite--header-4">Returns</h4>
<p class="d-docsite--paragraph"><code v-pre>string</code></p>
<p class="d-docsite--paragraph">The formatted date string.</p>
<h3 id="formatmedium" tabindex="-1" class="d-docsite--header-3">formatMedium <a class="header-anchor d-link d-docsite--link d-link" href="#formatmedium" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph"><strong>formatMedium</strong>(<code v-pre>date</code>, <code v-pre>locale</code>): <code v-pre>string</code></p>
<p class="d-docsite--paragraph">Formats the given date in medium format.</p>
<h4 class="d-docsite--header-4">Parameters</h4>
<table>
<thead>
<tr>
<th style="text-align:left">Name</th>
<th style="text-align:left">Type</th>
<th style="text-align:left">Description</th>
</tr>
</thead>
<tbody>
<tr>
<td style="text-align:left"><code v-pre>date</code></td>
<td style="text-align:left"><code v-pre>Date</code></td>
<td style="text-align:left">The date to format</td>
</tr>
<tr>
<td style="text-align:left"><code v-pre>locale</code></td>
<td style="text-align:left"><code v-pre>string</code></td>
<td style="text-align:left">The locale to use for formatting. Defaults to Dialtone preferred locale.</td>
</tr>
</tbody>
</table>
<h4 class="d-docsite--header-4">Returns</h4>
<p class="d-docsite--paragraph"><code v-pre>string</code></p>
<p class="d-docsite--paragraph">The formatted date string.</p>
<h3 id="formatshort" tabindex="-1" class="d-docsite--header-3">formatShort <a class="header-anchor d-link d-docsite--link d-link" href="#formatshort" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph"><strong>formatShort</strong>(<code v-pre>date</code>, <code v-pre>locale</code>, <code v-pre>showWeekday</code>= true): <code v-pre>string</code></p>
<p class="d-docsite--paragraph">Formats a date into a short string representation.</p>
<h4 class="d-docsite--header-4">Parameters</h4>
<table>
<thead>
<tr>
<th style="text-align:left">Name</th>
<th style="text-align:left">Type</th>
<th style="text-align:left">Description</th>
</tr>
</thead>
<tbody>
<tr>
<td style="text-align:left"><code v-pre>date</code></td>
<td style="text-align:left"><code v-pre>Date</code></td>
<td style="text-align:left">The date to format</td>
</tr>
<tr>
<td style="text-align:left"><code v-pre>locale</code></td>
<td style="text-align:left"><code v-pre>string</code></td>
<td style="text-align:left">The locale to use for formatting. Defaults to Dialtone preferred locale.</td>
</tr>
<tr>
<td style="text-align:left"><code v-pre>showWeekday</code></td>
<td style="text-align:left"><code v-pre>boolean</code></td>
<td style="text-align:left">Whether to include the weekday in the formatted string. Defaults to true.</td>
</tr>
</tbody>
</table>
<h4 class="d-docsite--header-4">Returns</h4>
<p class="d-docsite--paragraph"><code v-pre>string</code></p>
<p class="d-docsite--paragraph">The formatted date string.</p>
<h3 id="formatnoyear" tabindex="-1" class="d-docsite--header-3">formatNoYear <a class="header-anchor d-link d-docsite--link d-link" href="#formatnoyear" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph"><strong>formatNoYear</strong>(<code v-pre>date</code>, <code v-pre>locale</code>, <code v-pre>abbreviated</code>= false): <code v-pre>string</code></p>
<p class="d-docsite--paragraph">Formats a date without the year.</p>
<h4 class="d-docsite--header-4">Parameters</h4>
<table>
<thead>
<tr>
<th style="text-align:left">Name</th>
<th style="text-align:left">Type</th>
<th style="text-align:left">Description</th>
</tr>
</thead>
<tbody>
<tr>
<td style="text-align:left"><code v-pre>date</code></td>
<td style="text-align:left"><code v-pre>Date</code></td>
<td style="text-align:left">The date to format</td>
</tr>
<tr>
<td style="text-align:left"><code v-pre>locale</code></td>
<td style="text-align:left"><code v-pre>string</code></td>
<td style="text-align:left">The locale to use for formatting. Defaults to Dialtone preferred locale.</td>
</tr>
<tr>
<td style="text-align:left"><code v-pre>abbreviated</code></td>
<td style="text-align:left"><code v-pre>boolean</code></td>
<td style="text-align:left">Whether to use abbreviated month names. Defaults to false.</td>
</tr>
</tbody>
</table>
<h4 class="d-docsite--header-4">Returns</h4>
<p class="d-docsite--paragraph"><code v-pre>string</code></p>
<p class="d-docsite--paragraph">The formatted date without the year.</p>
<h3 id="formatnumerical" tabindex="-1" class="d-docsite--header-3">formatNumerical <a class="header-anchor d-link d-docsite--link d-link" href="#formatnumerical" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph"><strong>formatNumerical</strong>(<code v-pre>date</code>, <code v-pre>locale</code>): <code v-pre>string</code></p>
<p class="d-docsite--paragraph">Formats a date into a numerical string representation.</p>
<h4 class="d-docsite--header-4">Parameters</h4>
<table>
<thead>
<tr>
<th style="text-align:left">Name</th>
<th style="text-align:left">Type</th>
<th style="text-align:left">Description</th>
</tr>
</thead>
<tbody>
<tr>
<td style="text-align:left"><code v-pre>date</code></td>
<td style="text-align:left"><code v-pre>Date</code></td>
<td style="text-align:left">The date to format</td>
</tr>
<tr>
<td style="text-align:left"><code v-pre>locale</code></td>
<td style="text-align:left"><code v-pre>string</code></td>
<td style="text-align:left">The locale to use for formatting. Defaults to Dialtone preferred locale.</td>
</tr>
</tbody>
</table>
<h4 class="d-docsite--header-4">Returns</h4>
<p class="d-docsite--paragraph"><code v-pre>string</code></p>
<p class="d-docsite--paragraph">The formatted numerical date string.</p>
<h2 id="accessibility" tabindex="-1" class="d-docsite--header-2">Accessibility <a class="header-anchor d-link d-docsite--link d-link" href="#accessibility" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">Keyboard navigation with arrow and tab keys for datepicker component.</p>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">
<p class="d-docsite--paragraph">It will switch between <code v-pre>month-year-picker</code> and <code v-pre>calendar</code> with <code v-pre>TAB</code>.</p>
</li>
<li class="d-docsite--list-element">
<p class="d-docsite--paragraph">It will move around all calendar days with <code v-pre>arrow-keys</code>.</p>
</li>
<li class="d-docsite--list-element">
<p class="d-docsite--paragraph">It will jump from <code v-pre>month-year-picker</code> to <code v-pre>calendar</code> with <code v-pre>DOWN</code> arrow key.</p>
</li>
<li class="d-docsite--list-element">
<p class="d-docsite--paragraph">It will jump to <code v-pre>month-year-picker</code> when there are no more days at the bottom or top of the calendar.</p>
</li>
<li class="d-docsite--list-element">
<p class="d-docsite--paragraph">It will change year or month with <code v-pre>ENTER</code> or <code v-pre>SPACE</code> (native event)</p>
</li>
<li class="d-docsite--list-element">
<p class="d-docsite--paragraph">It will select day with <code v-pre>ENTER</code> or <code v-pre>SPACE</code> (native event)</p>
</li>
<li class="d-docsite--list-element">
<p class="d-docsite--paragraph">It will start with focus on previous-year on mounted. Screen reader announcement:</p>
<div class="language-text" data-highlighter="prismjs" data-ext="text"><pre v-pre><code class="language-text"><span class="line">In `month-year-picker`:</span>
<span class="line"></span>
<span class="line">- Change to previous year, 2022</span>
<span class="line">- Change to previous month, july</span>
<span class="line">- Change to next month, september</span>
<span class="line">- Change to next year, 2024</span>
<span class="line"></span>
<span class="line">In `calendar`:</span>
<span class="line"></span>
<span class="line">- Select day 20 July 2023</span>
<span class="line"></span></code></pre>
</div></li>
</ul>
</div></template>


<script setup>
import { ref, inject } from 'vue';

const dialtoneUtils = inject('dialtoneUtils');
const { formatLong, formatMedium, formatShort, formatNoYear, formatNumerical } = dialtoneUtils;

const currentSelectedDate = ref(new Date());
const datepickerOpened = ref(false);

const today = new Date();
const minDate = ref(new Date(today.getFullYear(), today.getMonth(), today.getDate() - 5));
const maxDate = ref(new Date(today.getFullYear(), today.getMonth(), today.getDate() + 15));

const toggleDatepicker = () => {
  datepickerOpened.value = !datepickerOpened.value;
};

</script>